import { useState } from 'react';
import { changePassword } from '@/lib/admin';
import { Field, TextInput } from './fields';

const RULES = [
    { test: (p) => p.length >= 10, label: 'At least 10 characters' },
    { test: (p) => /[A-Za-z]/.test(p), label: 'Contains a letter' },
    { test: (p) => /\d/.test(p), label: 'Contains a digit' },
    { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'Contains a symbol (e.g. ! ? @ #)' },
];

export default function Account() {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null); // {kind:'ok'|'err', text}

    const policy = RULES.map((r) => ({ ...r, ok: r.test(next) }));
    const policyOk = policy.every((r) => r.ok);
    const matchOk = next.length > 0 && next === confirm;
    const canSubmit = current.length > 0 && policyOk && matchOk && !busy;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true); setMsg(null);
        try {
            await changePassword(current, next);
            setMsg({ kind: 'ok', text: 'Password updated. Your current session stays active — next time you sign in, use the new password.' });
            setCurrent(''); setNext(''); setConfirm('');
        } catch (err) {
            const detail = err.response?.data?.detail;
            setMsg({ kind: 'err', text: typeof detail === 'string' ? detail : 'Could not update password.' });
        } finally { setBusy(false); }
    };

    return (
        <div className="flex flex-col gap-10" data-testid="account-page">
            <header className="flex flex-col gap-3">
                <span className="label" style={{ color: 'var(--clay)' }}>§ Account</span>
                <h1 className="serif" style={{ fontSize: 42, letterSpacing: '-0.02em' }}>Change password</h1>
                <p className="caption" style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--ink-2)', maxWidth: 640 }}>
                    Set a new password for the admin account. You&rsquo;ll stay signed in on this device — the new password is required for any future sign-in.
                </p>
            </header>

            <form onSubmit={submit} className="flex flex-col gap-8 max-w-xl">
                <Field label="Current password">
                    <TextInput
                        type="password"
                        autoComplete="current-password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        data-testid="account-current"
                    />
                </Field>
                <Field label="New password">
                    <TextInput
                        type="password"
                        autoComplete="new-password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        data-testid="account-new"
                    />
                </Field>

                {/* Live policy checklist */}
                <ul data-testid="account-policy" className="flex flex-col gap-2" style={{ marginTop: -4 }}>
                    {policy.map((r) => (
                        <li
                            key={r.label}
                            className="caption"
                            style={{
                                fontSize: 13,
                                color: r.ok ? 'var(--ink)' : 'var(--ink-3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                            }}
                        >
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.ok ? 'var(--clay)' : 'transparent', border: r.ok ? 'none' : '1px solid var(--ink-3)' }} />
                            {r.label}
                        </li>
                    ))}
                </ul>

                <Field label="Confirm new password">
                    <TextInput
                        type="password"
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        data-testid="account-confirm"
                    />
                </Field>
                {confirm.length > 0 && !matchOk && (
                    <span className="caption" style={{ fontSize: 13, color: 'var(--clay-deep)' }}>
                        Confirmation does not match.
                    </span>
                )}

                <div className="flex items-center gap-8 mt-2">
                    <button type="submit" disabled={!canSubmit} className="btn-primary" data-testid="account-submit">
                        {busy ? 'Updating…' : 'Update password'}
                    </button>
                </div>

                {msg && (
                    <div
                        data-testid={msg.kind === 'ok' ? 'account-success' : 'account-error'}
                        className="caption rule-top pt-6"
                        style={{
                            fontSize: 15,
                            color: msg.kind === 'ok' ? 'var(--ink)' : 'var(--clay-deep)',
                            maxWidth: 600,
                        }}
                    >
                        {msg.text}
                    </div>
                )}
            </form>
        </div>
    );
}
